import React, { useEffect, useMemo, useRef, useState } from 'react';
import { navigateToWell } from '../utils/wellIdentity';
import { createDebouncedTracker, track } from '../lib/track';

function matchesQuery(w, q) {
  if (!q) return true;
  const hay = [w.lease, w.well, w.api, w.propnum, w.county, w.status, w.wellType]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((tok) => hay.includes(tok));
}

/**
 * Searchable well picker. Navigates to #well/… on select.
 *
 * wells: array of { lease, api, propnum?, status?, county?, wellType?, modeled? }
 */
export default function WellSelector({
  wells = [],
  currentWellId = '',
  placeholder = 'Search wells by name, API, county, status…',
  autoFocus = false,
  size = 'md',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const trackSearch = useRef(createDebouncedTracker('well_search_used', 500)).current;

  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = wells.filter((w) => matchesQuery(w, q));
    return list.sort((a, b) => {
      const ap = a.pv10M ?? a.pv10Display ?? -Infinity;
      const bp = b.pv10M ?? b.pv10Display ?? -Infinity;
      if (Number.isFinite(bp) && Number.isFinite(ap) && bp !== ap) return bp - ap;
      return String(a.lease || '').localeCompare(String(b.lease || ''));
    });
  }, [wells, query]);

  const currentLabel = useMemo(() => {
    if (!currentWellId) return '';
    const id = String(currentWellId).toLowerCase();
    const hit = wells.find(
      (w) =>
        String(w.api || '').replace(/\D/g, '').startsWith(id.replace(/\D/g, '')) ||
        String(w.propnum || '').toLowerCase() === id ||
        String(w.lease || '').toLowerCase() === id,
    );
    return hit ? hit.lease : '';
  }, [wells, currentWellId]);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  function select(w) {
    track('well_search_used', {
      action: 'select',
      query_len: query.trim().length,
      result_count: sorted.length,
      selected_api: w.api || undefined,
      selected_lease: w.lease || w.well || undefined,
      source: 'well_selector',
    });
    setOpen(false);
    setQuery('');
    navigateToWell(w);
  }

  function onKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(sorted.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && sorted[activeIdx]) {
      e.preventDefault();
      select(sorted[activeIdx]);
    }
  }

  return (
    <div className={`well-selector well-selector-${size}`} ref={rootRef}>
      <label className="well-selector-label">
        <span>Find a well</span>
        <input
          ref={inputRef}
          type="search"
          className="well-selector-input"
          placeholder={currentLabel ? `Current: ${currentLabel} — ${placeholder}` : placeholder}
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            setOpen(true);
            const q = next.trim();
            if (q.length >= 2) {
              trackSearch({
                action: 'query',
                query_len: q.length,
                result_count: wells.filter((w) => matchesQuery(w, q.toLowerCase())).length,
                source: 'well_selector',
              });
            }
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="well-selector-list"
        />
      </label>
      {open && (
        <ul id="well-selector-list" className="well-selector-list" role="listbox">
          {sorted.length === 0 ? (
            <li className="well-selector-empty">No wells match “{query}”</li>
          ) : (
            sorted.slice(0, 40).map((w, i) => {
              const pv = w.pv10M ?? w.pv10Display;
              const pvLabel =
                pv == null || Number.isNaN(pv)
                  ? '—'
                  : `$${(pv > 100 ? pv / 1000 : pv).toFixed(2)}MM`;
              return (
                <li key={`${w.api || w.propnum || w.lease}-${i}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === activeIdx}
                    className={`well-selector-option ${i === activeIdx ? 'active' : ''}`}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => select(w)}
                  >
                    <span className="well-selector-option-main">
                      <strong>{w.lease || w.well}</strong>
                      <span className="well-selector-meta">
                        {[w.status, w.wellType, w.county].filter(Boolean).join(' · ')}
                        {w.api ? ` · ${w.api}` : ''}
                      </span>
                    </span>
                    <span className="well-selector-pv">{pvLabel}</span>
                  </button>
                </li>
              );
            })
          )}
          {sorted.length > 40 && (
            <li className="well-selector-empty">Showing top 40 of {sorted.length} — refine search</li>
          )}
        </ul>
      )}
    </div>
  );
}
